/**
 * @fileoverview SessionCard Header Component
 * 
 * Displays the header section of a session card including student name,
 * notification badges, and action controls.
 * 
 * @module vtc/components/SessionCard/SessionCardHeader
 */

import {
  IconExpandItemsLine,
  Menu,
  IconExternalLinkLine,
  IconNoLine,
  IconButton,
  IconSettingsLine,
  IconCommentLine,
  IconEyeLine,
  IconRefreshLine,
} from '@instructure/ui';
import { Flex, Text, View, Badge } from '@smarterservices/smarter-ui';
import Card from '../Common/Card';
import SessionFocusIndicator from './SessionFocusIndicator';
import SessionChatIndicator from './SessionChatIndicator';
import SessionFlagIndicator from './SessionFlagIndicator';
import SessionHandRaise from './SessionHandRaise';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import { useSessionCardState } from './useSessionCardState';

/**
 * SessionCardHeader Component
 * 
 * Renders the header section with student name, badges, and action buttons.
 * Changes appearance based on focus state (blue when unfocused, red when focused).
 * Gets all data directly from stores to minimize prop drilling and re-renders.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.sessionSid - Session identifier
 * @param {Object} props.data - Session data
 * @returns {JSX.Element}
 */
function SessionCardHeader({ sessionSid, data }) {
  // Get all state from the custom hook
  const {
    isFocused,
    allowFocusOption,
    unReadMessageCount,
    flagCount,
    handleTransfer,
    handleEndSession,
    handleOpenChat,
    handleFocus,
  } = useSessionCardState(sessionSid);
  return (
    <Card.Header background={isFocused ? 'danger' : 'primary'}>
      <Card.Title>
        {!isFocused ? (
          <>
            {/* Student name display */}
            <Text weight="bold">
              {data?.firstName} {data?.lastName}
            </Text>
            <Text>&nbsp;({data?.sessionSid?.slice(-5)})</Text>
          </>
        ) : (
          /* Focused state label */
          <Text weight="bold">Focused</Text>
        )}
      </Card.Title>

      <Flex.Item shouldGrow margin='none none none small'  >


        {/* Chat indicator - shows who is chatting in this session */}
        <View>
          <SessionChatIndicator sessionSid={data?.sessionSid} />
        </View>
        <View>
          <SessionFlagIndicator sessionSid={data?.sessionSid} />
        </View>
        <View>
          <SessionHandRaise sessionSid={data?.sessionSid} />
        </View>
      </Flex.Item>
      <Flex.Item margin='none none none small'  >
        {/* Focus indicator - shows who is focused on this session */}
        <View padding="none x-small none none">
          <SessionFocusIndicator
            sessionSid={data?.sessionSid}
          />
        </View>
      </Flex.Item>
      {/* Action Controls - Only shown when not focused */}
      {!isFocused && (
        <Card.Actions>
          {/* Flag count badge */}
          {flagCount > 0 && (
            <Badge
              standalone
              variant="danger"
              pulse={true}
              count={flagCount}
              countUntil={10}
              margin="0 xx-small 0 0"
            />
          )}

          {/* Unread messages button */}
          {unReadMessageCount > 0 && (
            <IconButton
              withBackground={false}
              withBorder={false}
              color="danger"
              size="small"
              screenReaderLabel="Unread Messages"
              margin="none"
              padding="none"
              onClick={onOpenChat}
            >
              <IconCommentLine />
            </IconButton>
          )}

          {/* Settings menu */}
          <Menu
            placement="bottom end"
            shouldContainFocus
            shouldReturnFocus
            mountNode={() => document.body}
            trigger={
              <IconButton
                withBackground={false}
                withBorder={false}
                color="primary"
                size="small"
                screenReaderLabel="Session Options"
                margin="none"
                padding="none"
              >
                <IconSettingsLine />
              </IconButton>
            }
          >
            {/* Transfer session */}
            <Menu.Item onSelect={handleTransfer}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <IconExternalLinkLine size="x-small" />
                </Flex.Item>
                <Flex.Item>
                  <Text>Transfer</Text>
                </Flex.Item>
              </Flex>
            </Menu.Item>
            {/* End session */}
            <Menu.Item onSelect={handleEndSession}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <IconNoLine size="x-small" />
                </Flex.Item>
                <Text>End Session</Text>
              </Flex>
            </Menu.Item>
            <Menu.Separator />
            {/* Reinitialize session */}
            <Menu.Item onSelect={() => useSessionStore.getState().reinitializeSession(data?.sessionSid)}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <IconRefreshLine size="x-small" />
                </Flex.Item>
                <Text>Reload Session</Text>
              </Flex>
            </Menu.Item>
          </Menu>

          {/* Focus mode button */}
          {allowFocusOption && (
            <IconButton
              withBackground={false}
              withBorder={false}
              color="primary"
              size="small"
              screenReaderLabel="Expand To Focus Mode"
              margin="none"
              padding="none"
              onClick={handleFocus}
            >
              <View
                display="inline-block"
                style={{ transform: 'rotate(45deg)' }}
              >
                <IconExpandItemsLine />
              </View>
            </IconButton>
          )}
        </Card.Actions>
      )}
    </Card.Header>
  );
}

export default SessionCardHeader;

